<template>
  <!-- prettier-ignore -->
  <div
    class="ovp-configurator ovp-a11y-container"
    :app-language="selectedLanguage"
    :app-name="$props.source"
    :app-version="version$"
  >
    <template v-if="isApplicationReady">
      <div class="ovp-header">
        <the-header :meta-options="routeMeta" />
      </div>

      <div class="ovp-content">
        <div
          v-if="showNavigation"
          class="ovp-navigation"
        >
          <the-navigation />
        </div>

        <system-notifications
          :key="`notifications-${selectedLanguage}`"
          class="system-notifications"
          app="dvp"
          :base-url="ressourceURI"
          :language="selectedLanguage"
        />

        <div class="ovp-container">
          <router-view v-slot="{ Component }">
            <transition
              mode="out-in"
              :name="routeTransition"
            >
              <div :key="Component?.type.__name || route.name">
                <component :is="Component" />
              </div>
            </transition>
          </router-view>
        </div>

        <medical-office-search />

        <div class="ovp-footer">
          <the-footer :meta-options="routeMeta" />
          <save-nudge v-if="isNudgingEnabled" />
        </div>
      </div>

      <div id="modals"></div>
      <the-confirmation />
      <the-appointment />
      <fab-container />
      <screen-auth />
      <login-modal />
    </template>

    <the-loading />
    <global-error-handler />
    <quiz-modal />
    <the-survey />
    <notifications />
    <the-toasts />
  </div>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { registerUpdateBasketListeners } from '@/helpers/GlobalEventHandler'
import { logInfo } from '@/helpers/Logger'
import { events$, loading$ } from '@/services'

import basketStore from '@/store/basket'
import useApplication from '@/hooks/useApplication'
import useI18n from '@/hooks/useI18n'
import useSurvey from '@/hooks/useSurvey'
import useSync from '@/hooks/useSync'
import useUrl from '@/hooks/useUrl'

import FabContainer from '@/components/Fab/FabContainer'
import GlobalErrorHandler from '@/components/Container/GlobalErrorHandler'
import MedicalOfficeSearch from '@/components/MedicalSearch/MedicalOfficeSearch'
import Notifications from '@/components/Container/Notifications'
import QuizModal from '@/components/Quiz/QuizModal'
import SaveNudge from '@/components/Basket/SaveNudge/SaveNudge'
import ScreenAuth from '@/components/Screen/ScreenAuth'
import SystemNotifications from '@/components/Container/SystemNotifications'
import TheAppointment from '@/components/Modal/Appointment'
import TheConfirmation from '@/components/Modal/Confirmation'
import TheFooter from '@/components/Container/Footer'
import TheHeader from '@/components/Container/Header'
import TheLoading from '@/components/Container/Loading'
import TheNavigation from '@/components/Navigation/Navigation'
import TheSurvey from '@/components/Survey/Survey'
import TheToasts from '@/components/Container/Toasts'
import { LoginModal } from '@/components/Login'

import { EVENT_DOTCOM_SURVEY_WEBSITE, EVENT_OVP, EVENT_URL, SOURCE } from '@/config/events'
import { ROUTE_NAME } from '@/config/constants'

import '@/assets/css/colors.css'
import '@/assets/css/sizes.css'

// HOOKS
const { initializeClearSequence, isReadyWithoutPartner, isReadyWithPartner } = useApplication()
const { selectedLanguage } = useI18n()
const route = useRoute()
const router = useRouter()
const { triggerSurvey } = useSurvey()
const { registerSync, unregisterSync } = useSync()
const { react } = useUrl()

// INIT
const ressourceURI = import.meta.env.VITE_SANITAS_RESOURCE_URI
const version$ = inject('version$')
defineProps({
  source: {
    type: String,
    default: SOURCE.CONFIGURATOR,
  },
})

// DATA
const pristine = ref(true)
const routeTransition = ref('slide-left')
let routeConfirmed = ref(false)

// COMPUTED
const isApplicationReady = computed(() => {
  if (routeMeta.value.isReentry) {
    return isReadyWithoutPartner.value && routeConfirmed.value
  } else {
    return isReadyWithPartner.value && routeConfirmed.value
  }
})

const isNudgingEnabled = computed(() => {
  return basketStore.basket.persons?.length > 0 && !basketStore.basket.existingCustomer
})

const routeMeta = computed(() => route?.meta || {})

const showNavigation = computed(() => route.name && !route.meta.aide)

// METHODS
function emitEventWhenReady() {
  return new Promise(function (resolve) {
    if (isApplicationReady.value) resolve()

    const unwatch = watch(
      () => isApplicationReady.value,
      () => {
        unwatch()
        resolve()
      }
    )
  })
}

/**
 * Check, if the form has been touched, when unload is triggered
 * (so you don't loose unsaved changes)
 */
function unload(event) {
  const disabled = route.meta?.disableUnloadConfirmation || pristine.value
  if (disabled) return true

  const message = 'nothing'
  event.returnValue = message
  return message
}

// WATCHERS
watch(isApplicationReady, async value => {
  if (value) {
    logInfo(['%cLOCKED AND LOADED!', 'color: GREEN'])

    await nextTick()

    loading$.end()
  }
})

// LIFECYCLE HOOKS
onBeforeMount(() => {
  initializeClearSequence()

  loading$.start()

  window.addEventListener('beforeunload', unload)

  events$.once(EVENT_DOTCOM_SURVEY_WEBSITE, () => {
    triggerSurvey()
  })
})

onMounted(() => {
  registerUpdateBasketListeners()
  emitEventWhenReady().then(() => {
    // tab-sync needs to be registered, after all the BE-/localStorage operations are done
    registerSync()

    // react to URL-params (e.g. open login)
    react()
  })

  events$.on(EVENT_URL.GOTO, opts => {
    if (!opts.type) opts.type = 'push'

    // new route can come as a string, or as a route-object
    if (opts.target) {
      if (basketStore.basket.persons.length > 0) {
        router[opts.type](opts.target.replace(':personId', basketStore.basket.persons[0].personId))
      }
    } else {
      router[opts.type](opts.route)
    }
  })

  events$.once(EVENT_OVP.BASKET_PREFILLED, () => {
    const start = window.location.hash.indexOf('?')
    const query = window.location.hash.substring(start)
    const params = new URLSearchParams(query)

    const goto = params.get('goto')
    if (goto) {
      router.push(goto.replace(':personId', basketStore.basket.persons[0].personId))
    }
  })
})

onBeforeUnmount(() => {
  unregisterSync()
  window.removeEventListener('beforeunload', unload)
})

router.beforeEach((to, from) => {
  const __start = from.meta.index
  const __end = to.meta.index

  // from ovp to alva
  if (__start < 100 && __end >= 100) {
    routeTransition.value = 'slide-down'
    // from alva to ovp
  } else if (__start >= 100 && __end < 100) {
    routeTransition.value = 'slide-up'
    // normal
  } else {
    // @TODO: Figure out, why the transition is wonky, when navigating to onboarding-screen
    if (to.name === ROUTE_NAME.ONBOARDING) {
      routeTransition.value = null
    } else {
      routeTransition.value = to.meta.index < from.meta.index ? 'slide-right' : 'slide-left'
    }
  }

  return true
})

router.isReady().then(() => {
  routeConfirmed.value = true
})
</script>

<style name="animations" scoped>
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active,
.slide-up-enter-active,
.slide-up-leave-active,
.slide-down-enter-active,
.slide-down-leave-active {
  overflow: hidden;
  transition-duration: 0.5s;
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
}

.slide-left-enter-from,
.slide-right-leave-to {
  transform: translate(100%, 0);
}
.slide-left-leave-to,
.slide-right-enter-from {
  transform: translate(-100%, 0);
}

.slide-up-enter-from,
.slide-down-leave-to {
  transform: translate(0, 100%);
}
.slide-up-leave-to,
.slide-down-enter-from {
  transform: translate(0, -100%);
}
</style>

<style name="global">
*,
*:after,
*:before {
  box-sizing: border-box;
}

html {
  overflow-y: scroll;
}

body {
  margin: 0;
  font-family:
    Open Sans,
    sans-serif;
  font-size: var(--san-dvp-font-size-base);
  line-height: var(--san-dvp-line-height-base);
  color: var(--c-primary-neutral-1);
  /* FLEX-2954 prevent larger fonts for mobile landscape */
  -webkit-text-size-adjust: none;
}

a {
  display: inline-block;
  font-weight: bold;
  font-size: var(--san-dvp-font-size-base);
  line-height: var(--san-dvp-line-height-base);
  color: var(--c-primary-color-3);
  text-decoration: none;
  border-bottom: none;
  cursor: pointer;
}

a:hover {
  transition: color 0.3s ease-in-out;
  color: var(--c-primary-color-2);
}

button {
  font-weight: bold;
}

button,
input,
select,
textarea {
  font-family:
    Open Sans,
    sans-serif !important;
}

.modal-container .close-icon-container {
  z-index: 9999 !important;
  margin: 0 !important;

  position: absolute;
  right: 0;
  top: 0;
  cursor: pointer;
}

.tippy-box {
  border-radius: 0;
  background-color: var(--c-primary-neutral-1);

  .tippy-content {
    padding: 15px;

    ul {
      padding-left: 40px;

      li {
        color: var(--c-primary-neutral-3);
      }
    }
  }

  .tippy-arrow {
    color: var(--c-primary-neutral-1);
  }
}

.tippy-box[data-theme~='card'] {
  background-color: var(--c-primary-neutral-3);
  border-radius: 16px;
  padding: 0;
  margin: 0;
  box-shadow:
    0 3px 5px rgba(0, 0, 0, 0.2),
    0 1px 18px rgba(0, 0, 0, 0.12);

  & > .tippy-content {
    padding: 0;
    margin: 0;
    height: max-content;

    & > .card__image {
      margin: 0;
      padding: 0;
      width: 100%;
    }
  }
}

.tippy-box[data-placement='top-end'] > {
  .tippy-arrow {
    right: 20px !important;
    left: initial !important;
  }
}

.the-transition-enter-active,
.the-transition-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.the-transition-enter-from,
.the-transition-leave-to {
  opacity: 0;
}
</style>

<style name="mobile" scoped>
.ovp-content {
  position: relative;
  display: inline-block;
  overflow: hidden;
  min-height: 200px;
  width: 100%;
  background-color: var(--c-primary-neutral-2);
}

.ovp-container {
  width: 100%;
  transition: filter 0.3s ease-in-out;
}

.ovp-navigation {
  position: relative;
  z-index: 3;
  width: 100%;
  background-color: var(--c-primary-color-1);
}

.ovp-footer {
  width: 100%;
  background-color: var(--c-primary-neutral-3);

  .footer {
    width: 100%;
  }
}

.ovp-configurator--aide {
  background-color: var(--c-primary-neutral-2);

  .ovp-header {
    background-color: var(--c-primary-neutral-3);
  }

  .ovp-footer {
    background-color: var(--c-primary-neutral-2);
  }
}

.ovp-configurator--loading {
  pointer-events: none;
}

.system-notifications {
  margin: 15px auto;
  width: 100%;

  &:empty {
    display: none;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .ovp-footer .footer {
    min-width: 768px;
    max-width: 960px;
    margin: 0 auto;
  }

  .system-notifications {
    margin: 0 auto 15px auto;
    max-width: 960px;

    &:empty {
      display: none;
    }
  }
}
</style>
